export { constructInventoryDefinitionAssets as campsiteDefinitionAssets } from './compile/from-base-inventory-assets';
import { readInventoryField } from './compile/inventory-raw-readers';
import { readItemField } from './compile/item-field-reader';
import { ftShapeOverlayDoorStates, ftShapeOverlayConcealedDoors, ftShapeOverlayWallBreaches } from './port/k2-geometry/ft3d_shape_core';
import { array, native, record } from './port/k2-geometry/php-shapes';
import { ftFloorBandRoomInputs, ftFloorBandRoomFromInputs } from './port/k2-geometry/ft3d_floor_band';
import { validateShape } from '../../../ft3d/engine/ft3d-shape-core.js';

type Row = Record<string, unknown>;
const rows = (value: unknown): Row[] => Object.values((value ?? {}) as Record<string, Row>);
const parse = (value: unknown): Row => { try { return (typeof value === 'string' ? JSON.parse(value) : value) as Row ?? {}; } catch { return {}; } };
const inventoryField = (row: Row | undefined, key: string): unknown => readInventoryField({ inventory: row }, key);

export function campsiteFloorBand(shape: Row, position: { x: number; y: number; z: number }): number {
    const room = native(ftFloorBandRoomFromInputs(ftFloorBandRoomInputs(shape.boxes), position.x, position.y, position.z)) as Row | null;
    return Number(room?.floor ?? 0);
}

/** Pure lab adapter around native shape rules. Every dependency is saved input; no compiler/store/IO. */
export function campsiteShape(shapeInput: Row, inventory: Row[], doorRows: Row[], catalog: Record<string, Row>): Row {
    const inventories = Object.fromEntries(inventory.map(row => [String(readInventoryField({ inventory: row }, 'inventory_id')).toUpperCase(), row]));
    const states = Object.fromEntries(doorRows.map(row => [String(readItemField(row, 'opening_id')), {
        open: Number(readItemField(row, 'door_open')) === 1, cleared: Number(readItemField(row, 'glass_cleared')) === 1,
    }]));
    const deps = {
        check_uuid: (id: unknown) => typeof id === 'string' && /^[a-f\d]{8}(?:-[a-f\d]{4}){3}-[a-f\d]{12}$/i.test(id),
        inventory_field: (row: unknown, key: string) => readInventoryField({ inventory: native(row) as Row }, key),
        item_field: (row: unknown, key: string) => readItemField(native(row) as Row | null, key),
        ft_shape_opening_vitality_rows: () => record(inventories),
        ft_shape_private_door_inventory_ids: (ids: unknown) => record(Object.fromEntries([...array(ids).values()].flatMap(id => {
            const row = inventories[String(id).toUpperCase()];
            return !row || Number(inventoryField(row, 'inventory_deleted')) !== 0 || Number(inventoryField(row, 'inventory_private')) !== 0 ? [[String(id), true]] : [];
        }))),
    };
    const env = { deps } as unknown as Parameters<typeof ftShapeOverlayDoorStates>[3];
    let shape = ftShapeOverlayDoorStates(shapeInput, states, [], env);
    const breaches = inventory.flatMap(row => {
        if (Number(readItemField(row, 'inventory_deleted')) !== 0) return [];
        const generation = parse(readItemField(row, 'inventory_generation'));
        const type = readItemField(catalog[String(readItemField(row, 'inventory_item_id'))] ?? null, 'item_type');
        const run = type === 'wall' && Number(readItemField(row, 'v_max')) > 0 && Number(readItemField(row, 'v_current')) <= 0
            ? generation.wallRun : generation.wallBreach;
        return run && typeof run === 'object' ? [{ ...run as Row, inventoryId: readItemField(row, 'inventory_id') }] : [];
    }).sort((a, b) => String((a as Row).key ?? '').localeCompare(String((b as Row).key ?? '')));
    shape = ftShapeOverlayWallBreaches(shape, breaches, { deps: { ft_shape_validate: value => {
        try { validateShape(native(value)); return true; } catch { return false; }
    } } } as Parameters<typeof ftShapeOverlayWallBreaches>[2]);
    const result = native(shape) as Row;
    for (const roof of rows((result.authoring as Row | undefined)?.buildingRoofs)) {
        if (!roof.inventoryId) continue;
        const row = inventories[String(roof.inventoryId).toUpperCase()];
        if (!row || Number(inventoryField(row, 'inventory_deleted')) === 1 || Number(inventoryField(row, 'item_qty')) <= 0
            || inventoryField(row, 'v_current') != null && Number(inventoryField(row, 'v_current')) <= 0)
            for (const box of rows(result.boxes)) if ((roof.roomIds as string[]).includes(String(box.id))) {
                box.creation = { ...(box.creation as Row), roofRemoved: true };
            }
    }
    return native(ftShapeOverlayConcealedDoors(result, env)) as Row;
}

